import { Router, NavigationEnd } from '@angular/router';
import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VendorConsoleService } from 'src/app/pages/vendor-console/vendor-console.service';
import {
  RESTRICTED_MENU_EMAILS,
  HIDDEN_VENDOR_REPORTS_EMAILS,
} from 'src/app/restricted-email.guard';
import {
  AnalyticsService,
  CLIENT_EVENTS,
  VENDOR_EVENTS,
} from 'src/app/shared/analytics';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();
  visibility: string = 'external';
  visibilityExternal: boolean = false;
  visibilityVendor: boolean = false;
  visibilityCustomer: boolean = false;
  /** Surfaces the shared restricted-email check (defined once in
   *  restricted-email.guard.ts). Drives the *ngIf on Customers + Sales
   *  Console menu items. The route guard uses the same list so the URL
   *  can't be typed in to bypass the menu hide. */
  isProdController: boolean = false;
  /** True when the current user's email is on HIDDEN_VENDOR_REPORTS_EMAILS
   *  (env-driven). Drives the *ngIf on the Vendor Reports menu item; the
   *  matching route guard blocks URL typing too. */
  hideVendorReports: boolean = false;
  team: string = '';
  showFiller = false;
  isLoggedIn = false;
  notifications: any[] = [];
  unreadCount: number = 0;
  userEmail: string = '';
  companyName: string = '';
  private contributorId: number | null = null;
  private notificationInterval: any = null;

  constructor(public dialog: MatDialog, public router: Router, private apiService: ApiService, private snackBar: MatSnackBar, private vendorService: VendorConsoleService, private analytics: AnalyticsService) { }

  ngOnInit() {
    this.checkAuthStatus();
    const userInfoString = localStorage.getItem('userInfo');
    if (userInfoString) {
      const userInfo = JSON.parse(userInfoString);
      this.userEmail = userInfo.email;
      this.visibility = userInfo['custom:visibility'] || 'local';
      this.visibilityExternal = this.visibility === 'customer' || this.visibility === 'vendor';
      this.visibilityVendor = this.visibility === 'vendor';
      this.visibilityCustomer = this.visibility === 'customer';
      this.isProdController = RESTRICTED_MENU_EMAILS.has(
        (this.userEmail || '').toLowerCase(),
      );
      this.hideVendorReports = HIDDEN_VENDOR_REPORTS_EMAILS.has(
        (this.userEmail || '').toLowerCase(),
      );
      this.team = userInfo['custom:team'] || '';
      this.companyName = userInfo.company
    }

    if (this.isLoggedIn && this.visibilityVendor) {
      this.loadContributorAndFetchNotifications();
      // Poll for unread count every 60 seconds
      this.notificationInterval = setInterval(() => this.fetchVendorUnreadCount(), 60000);
    } else if (this.isLoggedIn) {
      this.fetchNotifications();
      this.notificationInterval = setInterval(() => this.fetchNotifications(), 600000);
    }


    // Listen for route changes to update auth status without refresh
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.checkAuthStatus();
      }
    });
  }

  checkAuthStatus() {
    const accessToken = localStorage.getItem('accessToken');
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    this.isLoggedIn = !!(accessToken && tokenExpiry && Date.now() < +tokenExpiry);

    const userInfoString = localStorage.getItem('userInfo');
    if (userInfoString) {
      const userInfo = JSON.parse(userInfoString);
      this.userEmail = userInfo.email;
      this.visibility = userInfo['custom:visibility'] || 'local';
      this.visibilityExternal =
        this.visibility === 'customer' || this.visibility === 'vendor';
      this.visibilityVendor = this.visibility === 'vendor';
      this.visibilityCustomer = this.visibility === 'customer';
      this.isProdController = RESTRICTED_MENU_EMAILS.has(
        (this.userEmail || '').toLowerCase(),
      );
      this.hideVendorReports = HIDDEN_VENDOR_REPORTS_EMAILS.has(
        (this.userEmail || '').toLowerCase(),
      );
      this.team = (userInfo['custom:team'] || userInfo['team'] || '').toLowerCase();
    } else {
      this.userEmail = '';
      this.visibility = 'local';
      this.visibilityExternal = false;
      this.visibilityVendor = false;
      this.isProdController = false;
      this.hideVendorReports = false;
      this.team = '';
    }
  }

  fetchNotifications() {
    if (!this.userEmail) return;

    this.apiService.get(`projects?notifications=true&email=${encodeURIComponent(this.userEmail)}`)
      .subscribe(
        (response: any) => {
          this.notifications = response.notifications || [];
          this.unreadCount = response.unreadCount || 0;
        },
        (error) => {
          console.error('Error fetching notifications:', error);
        }
      );
  }

  markAsRead(notificationId: string | number) {
    if (this.visibilityVendor) {
      this.markVendorNotificationAsRead(notificationId as number);
      return;
    }

    const notification = this.notifications.find(n => n.id === notificationId);
    if (!notification || !notification.project?.id) return;

    const projectId = notification.project.id;
    const payload = { email: this.userEmail };

    this.apiService.patch(`projects/${projectId}/notifications/${notificationId}/read`, payload)
      .subscribe(
        () => {
          if (notification.readBy[this.userEmail] === false) {
            notification.readBy[this.userEmail] = true;
            this.unreadCount = Math.max(0, this.unreadCount - 1);
          }
        },
        (error) => {
          console.error('Error marking notification as read:', error);
        }
      );
  }

  // ─── Vendor Notifications ─────────────────────────────────────
  loadContributorAndFetchNotifications() {
    this.vendorService.getVendorProfile().subscribe((profile) => {
      if (profile && profile.contributor_id) {
        this.contributorId = profile.contributor_id;
        this.fetchVendorNotifications();
      }
    });
  }

  fetchVendorNotifications() {
    if (!this.contributorId) return;
    this.vendorService.getVendorNotifications(this.contributorId).subscribe(
      (notifications) => {
        this.notifications = notifications;
        this.unreadCount = notifications.filter((n: any) => !n.is_read).length;
      }
    );
  }

  fetchVendorUnreadCount() {
    if (!this.contributorId) return;
    this.vendorService.getVendorUnreadCount(this.contributorId).subscribe(
      (count) => { this.unreadCount = count; }
    );
  }

  markVendorNotificationAsRead(notificationId: number) {
    if (!this.contributorId) return;
    const notification = this.notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    notification.is_read = 1;
    this.unreadCount = Math.max(0, this.unreadCount - 1);

    this.vendorService.markVendorNotificationAsRead(this.contributorId, notificationId).subscribe(
      () => { },
      (error) => {
        notification.is_read = 0;
        this.unreadCount += 1;
      }
    );
  }

  markAllVendorNotificationsAsRead() {
    if (!this.contributorId) return;
    this.vendorService.markAllVendorNotificationsAsRead(this.contributorId).subscribe(
      () => {
        this.notifications.forEach(n => n.is_read = 1);
        this.unreadCount = 0;
      }
    );
  }

  ngOnDestroy() {
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }
  }


  isToday(dateString: string): boolean {
    if (!dateString) return false;
    const today = new Date();
    const date = new Date(dateString);
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  isYesterday(dateString: string): boolean {
    if (!dateString) return false;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const date = new Date(dateString);
    return (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    );
  }

  // formatNotificationDate(dateString: string): string {
  //   if (!dateString) return '';
  //   const date = new Date(dateString);
  //   const now = new Date();
  //   const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  //   if (diffInDays === 0) {
  //     return 'Today';
  //   } else if (diffInDays === 1) {
  //     return 'Yesterday';
  //   } else if (diffInDays < 7) {
  //     return date.toLocaleDateString('en-US', { weekday: 'long' });
  //   } else {
  //     return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  //   }
  // }
  formatRelativeTime(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Calculate time differences
    const intervals = {
      y: 31536000,
      w: 604800,
      d: 86400,
      h: 3600,
      m: 60,
      s: 1
    };

    // Find the largest unit
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);

      if (interval >= 1) {
        // Return the largest unit that has at least 1
        return `${interval}${unit}`;
      }
    }

    return 'Just now';
  }

  logout() {
    // Determine the correct login page before clearing data
    const loginRoute = this.getLoginRouteForUser();

    try {
      if (this.visibilityVendor) {
        this.analytics.track(VENDOR_EVENTS.VENDOR_LOGGED_OUT);
      } else if (this.visibilityCustomer) {
        this.analytics.track(CLIENT_EVENTS.CLIENT_LOGGED_OUT);
      }
      this.analytics.reset();
    } catch { /* never block logout */ }

    // Clear all tokens and user info from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('profilePicture');
    localStorage.removeItem('tokenType');

    // Update login status
    this.isLoggedIn = false;

    // Redirect to the role-appropriate login page
    this.router.navigate([loginRoute]);
  }

  private getLoginRouteForUser(): string {
    if (this.visibilityVendor) return '/vendors/login';
    if (this.visibilityCustomer) return '/customers/login';
    return '/login';
  }
  /**
 * Checks if the New Brief button should be disabled
 * For customer visibility, checks if email and company name are present in userInfo
 */
  isNewBriefDisabled(): boolean {
    // Only apply validation for customer visibility
    if (!this.visibilityCustomer) {
      return false;
    }

    const userInfoString = localStorage.getItem('userInfo');
    if (!userInfoString) {
      return true; // Disable if no userInfo
    }

    try {
      const userInfo = JSON.parse(userInfoString);
      console.log('UserInfo for validation:', userInfo); // Add this for debugging

      const email = userInfo.email;
      // Try multiple possible keys for company name
      const companyName = userInfo.company || userInfo['custom:company_name'] || userInfo.companyName;

      console.log('Email present:', !!email);
      console.log('Company name present:', !!companyName);
      console.log('Company name value:', companyName);

      // Disable if either email or company name is missing/empty
      return !email || !companyName || email.trim() === '' || companyName.trim() === '';
    } catch (error) {
      console.error('Error parsing userInfo:', error);
      return true; // Disable on error
    }
  }

  /**
   * Returns appropriate tooltip text based on the disabled state reason
   */
  getNewBriefTooltip(): string {
    if (!this.visibilityCustomer) {
      return 'Create a new brief';
    }

    const userInfoString = localStorage.getItem('userInfo');
    if (!userInfoString) {
      return 'User information not found. Please log in again.';
    }

    try {
      const userInfo = JSON.parse(userInfoString);
      const email = userInfo.email;
      // Try multiple possible keys for company name
      const companyName = userInfo.company || userInfo['custom:company_name'] || userInfo.companyName;

      if (!email || email.trim() === '') {
        return 'Email address is missing. Please update your profile.';
      }

      if (!companyName || companyName.trim() === '') {
        return 'Company name is missing. Please update your profile.';
      }
    } catch (error) {
      return 'Unable to verify user information';
    }

    return 'Create a new brief';
  }

  isOnProfilePage(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.includes('/customers/profile');
  }
}